import { evaluateAnimatable, sortKeyframes, upsertKeyframe } from '../animation/keyframes.ts';
import { PARAM_DEFINITIONS, clampParam } from '../model/params.ts';
import type { Animatable, Clip, Easing, Id, ParamKey, Project } from '../model/schema.ts';
import { getClip } from '../timeline/queries.ts';
import { assertEditable } from './clip-ops.ts';
import { EditError, invariant } from './errors.ts';

function ensureParam(clip: Clip, key: ParamKey): Animatable {
  const def = PARAM_DEFINITIONS[key];
  invariant(def.appliesTo.includes(clip.type), `${def.label} does not apply to ${clip.type} clips`);
  let anim = clip.params[key];
  if (!anim) {
    anim = { value: def.defaultValue, keyframes: [] };
    clip.params[key] = anim;
  }
  return anim;
}

function editableClip(project: Project, clipId: Id): Clip {
  const { track, clip } = getClip(project, clipId);
  assertEditable(track);
  return clip;
}

/**
 * Sets a parameter. If the parameter is animated, a keyframe is written at `frame`
 * (relative to the clip start); otherwise the static value is changed.
 */
export function setParam(project: Project, clipId: Id, key: ParamKey, value: number, frame: number): void {
  const clip = editableClip(project, clipId);
  const anim = ensureParam(clip, key);
  const v = clampParam(key, value);
  if (anim.keyframes.length > 0) upsertKeyframe(anim, Math.round(frame), v);
  else anim.value = v;
}

/** Turns animation on (creating a keyframe at `frame` with the current value) or off. */
export function setParamAnimated(
  project: Project,
  clipId: Id,
  key: ParamKey,
  animated: boolean,
  frame: number,
): void {
  const clip = editableClip(project, clipId);
  const anim = ensureParam(clip, key);
  const current = evaluateAnimatable(anim, frame);
  if (animated) {
    if (anim.keyframes.length === 0) upsertKeyframe(anim, Math.round(frame), current);
  } else {
    anim.value = current;
    anim.keyframes = [];
  }
}

/** Adds a keyframe holding the current value at `frame` (no-op if one exists). */
export function addKeyframe(project: Project, clipId: Id, key: ParamKey, frame: number): void {
  const clip = editableClip(project, clipId);
  const anim = ensureParam(clip, key);
  const f = Math.round(frame);
  if (!anim.keyframes.some((k) => k.frame === f)) upsertKeyframe(anim, f, evaluateAnimatable(anim, f));
}

function findKeyframe(clip: Clip, keyframeId: Id) {
  for (const [key, anim] of Object.entries(clip.params)) {
    const kf = anim?.keyframes.find((k) => k.id === keyframeId);
    if (anim && kf) return { key: key as ParamKey, anim, kf };
  }
  throw new EditError('Keyframe not found');
}

export function removeKeyframes(project: Project, clipId: Id, keyframeIds: Id[]): void {
  const clip = editableClip(project, clipId);
  for (const id of keyframeIds) {
    const { anim, kf } = findKeyframe(clip, id);
    if (anim.keyframes.length === 1) anim.value = kf.value;
    anim.keyframes.splice(anim.keyframes.indexOf(kf), 1);
  }
}

/** Moves a keyframe to a new relative frame. Replaces any keyframe already at that frame. */
export function moveKeyframe(project: Project, clipId: Id, keyframeId: Id, frame: number): void {
  const clip = editableClip(project, clipId);
  const { anim, kf } = findKeyframe(clip, keyframeId);
  const f = Math.round(frame);
  anim.keyframes = anim.keyframes.filter((k) => k === kf || k.frame !== f);
  kf.frame = f;
  sortKeyframes(anim);
}

export function updateKeyframe(
  project: Project,
  clipId: Id,
  keyframeId: Id,
  patch: { value?: number; easing?: Easing },
): void {
  const clip = editableClip(project, clipId);
  const { key, kf } = findKeyframe(clip, keyframeId);
  if (patch.value !== undefined) kf.value = clampParam(key, patch.value);
  if (patch.easing !== undefined) kf.easing = patch.easing;
}

/** Resets a parameter to its default and removes its animation. */
export function resetParam(project: Project, clipId: Id, key: ParamKey): void {
  const clip = editableClip(project, clipId);
  delete clip.params[key];
}
